import {AccountSelection} from '@qep/shared/src';
import {type TraderDBAccount, type TradableSymbol, type SelectedAccount} from '@qep/types/src';
import React, {useEffect, useState} from 'react';
import './AccountSelectorOverwrites.scss';

type AccountSelectorDropdownProps = {
  accounts: TraderDBAccount[];
  tradableSymbol: TradableSymbol;
  handleChange: (value: TraderDBAccount | undefined, sym: TradableSymbol) => void;
};

/** Renders the authoritative default-account selection for a tradable symbol. */
const AccountSelectorDropdown: React.FC<AccountSelectorDropdownProps> = (props: AccountSelectorDropdownProps) => {
  const {tradableSymbol: symbol, accounts} = props;

  // @ts-expect-error Empty object will render as no selection - 'No default strategy'
  const [selectionInAccounts, setSelectionInAccounts] = useState<SelectedAccount | undefined>({});

  /** Mirrors authoritative account updates so API responses cannot leave the dropdown showing stale state. */
  useEffect(() => {
    const acc = accounts.find(acc => acc.id === symbol.defaultAccount);
    if (acc) {
      // @ts-expect-error Additional properties not required because account details can't be edited on this screen
      setSelectionInAccounts({account: acc});
    } else {
      // @ts-expect-error Empty object renders the supported "No default strategy" selection
      setSelectionInAccounts({});
    }
  }, [accounts, symbol.defaultAccount]);

  /** Propagates a resolved account, or undefined on clear so the parent removes the saved default. */
  const _handleChange = (value: any) => {
    if (value?.account === undefined || value.account.id === '') {
      props.handleChange(undefined, symbol);
    } else {
      const account: TraderDBAccount | undefined = accounts.find(acc => acc.id === value?.account?.id);
      if (account) {
        props.handleChange(account, symbol);
      }
    }
  };

  return (
    <div className='dropdown-container'>
      <AccountSelection
        accountsConfig={{
          accounts: accounts.filter(acc => acc.action === 'Trading'),
          productForFiltering: symbol,
        }}
        allowEmpty={true}
        blockCustomization={true}
        showSelectedLabel={false}
        selectedAccount={selectionInAccounts}
        onChange={_handleChange}
      />
    </div>
  );
};

export default AccountSelectorDropdown;
